import { useContext, useState, type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { getFirstSelectedTournament, orderSeriesTournaments } from 'app/utils/entities/series_data';
import { getTournamentBracketFetchUrl } from 'app/utils/entities/tournament';
import ConfigContext from 'app/common/ConfigContext';
import LoadingContainer from 'app/components/LoadingContainer';
import CircleLoader from 'app/components/CircleLoader/CircleLoader';
import Bracket from 'app/components/Blocks/BracketsBlock/Bracket';
import BlockGradTemplate from 'app/components/DesignVersion/Grad/Blocks/BlockGradTemplate';
import Tabs from 'app/components/Tabs';
import { usePersistentFetch } from 'app/hooks/usePersistentFetch';
import { ERROR_UNKNOWN } from 'app/constants/errors';

import style from './style.module.scss';

interface Props {
  data: SeriesStandingsBlockMatch;
}

const SeriesStandingsBlockGrad: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const configContext = useContext(ConfigContext);

  const [selectedTournament, setSelectedTournament] = useState<SeriesTournament | null>(
    getFirstSelectedTournament(data.tournaments) ?? null
  );

  // Use useFetch instead of useRequest to be able to use the cache-first fetch policy
  const {
    status,
    data: displayedTournament,
    error
  } = usePersistentFetch<TournamentBracket>({
    method: 'GET',
    fetchPolicy: 'cache-first',
    url: getTournamentBracketFetchUrl(selectedTournament!.id, configContext),
    preventRequest: !selectedTournament
  });

  const orderedTournaments = orderSeriesTournaments(t, data.tournaments);

  return (
    <BlockGradTemplate
      title={t('block-series_standings-title')}
      hasData={orderedTournaments.length > 0}
    >
      <Tabs
        compact
        tabs={orderedTournaments.map(tournament => tournament.name)}
        selectTab={tournamentName => {
          const tournament = orderedTournaments.find(t => t.name === tournamentName);
          if (tournament) {
            setSelectedTournament(tournament);
          }
        }}
        selectedTab={selectedTournament?.name ?? null}
        className={style.tabs}
      />
      <LoadingContainer
        data={displayedTournament}
        error={error ? ERROR_UNKNOWN : null}
        status={status}
        firstLoadIndicatorOnly
        loader={<CircleLoader size={20} className={style.loader} />}
      >
        <div className={style.bracketContainer}>
          <Bracket bracket={displayedTournament!} withTabs matchesListCollapsedByDefault />
        </div>
      </LoadingContainer>
    </BlockGradTemplate>
  );
};

SeriesStandingsBlockGrad.displayName = 'SeriesStandingsBlockGrad';

export default SeriesStandingsBlockGrad;
